import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  Upload,
  Search,
  Image,
  FileText,
  File,
  Lock,
  Globe,
  Trash2,
  Download,
  Link as LinkIcon,
  Eye,
  X,
  MoreVertical,
  FolderOpen,
  Unlink,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Layers,
  LayoutGrid,
  List,
  ArrowUpDown,
  Folder,
  GalleryHorizontalEnd,
  Bot,
  Edit3,
  Save,
  History
} from 'lucide-react';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import MarkdownViewer from '../../components/MarkdownViewer';
import DocEditorDrawer from '../../components/DocEditorDrawer';
import DocumentHistoryDialog from '../../components/DocumentHistoryDialog';

// File type detection
const getFileType = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  if (['txt', 'md'].includes(ext)) return 'text';
  return 'other';
};

// File type label
const getFileTypeLabel = (type) => {
  switch (type) {
    case 'image': return 'Image';
    case 'pdf': return 'PDF';
    case 'doc': return 'Document';
    case 'text': return 'Texte';
    default: return 'Autre';
  }
};

// File icon based on type
const FileIcon = ({ type, size = 24 }) => {
  switch (type) {
    case 'image': return <Image color="#00d4aa" size={size} />;
    case 'pdf': return <FileText color="#ef4444" size={size} />;
    case 'doc': return <FileText color="#3b82f6" size={size} />;
    case 'text': return <FileText color="#10b981" size={size} />;
    default: return <File color="#9ca3af" size={size} />;
  }
};

// Association badge colors
const associationColors = {
  section: { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' },
  item: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981' },
  general: { bg: 'rgba(156, 163, 175, 0.1)', color: '#9ca3af' }
};

// Document Card Component (reusable)
const DocumentCard = ({ doc, isAdmin, onView, onMenu, compact = false }) => {
  const fileType = getFileType(doc.name);
  const assocColor = associationColors[doc.association.type];

  return (
    <Paper
      sx={{
        p: compact ? 1.5 : 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: 2,
        transition: 'all 0.2s',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'rgba(0, 212, 170, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
      onClick={() => onView(doc)}
    >
      {/* Thumbnail / Icon */}
      <Box sx={{ 
        height: compact ? 80 : 120, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: 1,
        mb: compact ? 1 : 2,
        overflow: 'hidden'
      }}>
        {fileType === 'image' ? (
          <Box
            component="img"
            src={doc.url}
            alt={doc.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <FileIcon type={fileType} size={compact ? 32 : 48} />
        )}
      </Box>

      {/* Info */}
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, fontSize: compact ? '0.75rem' : '0.875rem' }} noWrap>
        {doc.name}
      </Typography>
      
      {!compact && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            {new Date(doc.updatedAt || doc.createdAt).toLocaleDateString('fr-FR')}
            {doc.updatedAt && new Date(doc.updatedAt).getTime() !== new Date(doc.createdAt).getTime() && ' (modifié)'}
          </Typography>
          {/* Créateur du document */}
          <Typography variant="caption" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontSize: '0.65rem' }}>
            Par: {doc.uploadedBy 
              ? `${doc.uploadedBy.firstName || ''} ${doc.uploadedBy.lastName || doc.uploadedBy.email}`.trim()
              : 'Système'
            }
          </Typography>
        </>
      )}

      {/* Badges */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 'auto' }}>
        {!compact && (
          <Chip
            label={doc.association.name}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              bgcolor: assocColor.bg,
              color: assocColor.color
            }}
          />
        )}
        {doc.isAdminOnly && (
          <Tooltip title="Document privé (Admin seulement)">
            <Chip
              icon={<Lock size={10} />}
              label={compact ? '' : 'Privé'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                ...(compact && { '& .MuiChip-label': { display: 'none' } })
              }}
            />
          </Tooltip>
        )}
        {doc.isPreview && (
          <Tooltip title="Planche Preview (inclus dans PDF)">
            <Chip
              icon={<GalleryHorizontalEnd size={10} />}
              label={compact ? '' : 'Preview'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                bgcolor: 'rgba(139, 92, 246, 0.1)',
                color: '#8b5cf6',
                ...(compact && { '& .MuiChip-label': { display: 'none' } })
              }}
            />
          </Tooltip>
        )}
      </Stack>

      {/* Actions */}
      {isAdmin && (
        <Box sx={{ mt: compact ? 0.5 : 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenu(e, doc);
            }}
            sx={{ 
              p: compact ? 0.25 : 0.5,
              opacity: compact ? 0.7 : 1,
              '&:hover': { opacity: 1 }
            }}
          >
            <MoreVertical size={compact ? 14 : 16} />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
};

// Section Group Component
const SectionGroup = ({ title, icon, docs, isAdmin, onView, onMenu, defaultOpen = true, level = 0 }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasContent = docs.length > 0;

  if (!hasContent) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          pl: level * 2 + 1.5,
          bgcolor: level === 0 ? 'rgba(0, 212, 170, 0.05)' : 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1,
          cursor: 'pointer',
          borderLeft: level > 0 ? '2px solid rgba(0, 212, 170, 0.3)' : 'none',
          '&:hover': { bgcolor: level === 0 ? 'rgba(0, 212, 170, 0.08)' : 'rgba(255, 255, 255, 0.04)' }
        }}
      >
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
          {title}
        </Typography>
        <Chip 
          label={docs.length} 
          size="small" 
          sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(255, 255, 255, 0.1)' }} 
        />
      </Box>
      <Collapse in={open}>
        <Grid container spacing={1.5} sx={{ mt: 1, pl: level * 2 }}>
          {docs.map((doc) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={doc.id}>
              <DocumentCard doc={doc} isAdmin={isAdmin} onView={onView} onMenu={onMenu} compact />
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Box>
  );
};

function DocumentsPage() {
  const { id: submissionId } = useParams();
  const { user } = useAuth();
  const { socket, joinSubmission, leaveSubmission } = useSocket();
  const fileInputRef = useRef();

  const [searchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section');

  const [documents, setDocuments] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [associationOptions, setAssociationOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Filters - initialize section filter from URL param if present
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState(sectionFromUrl ? `section-${sectionFromUrl}` : 'all');
  const [sortBy, setSortBy] = useState('date-desc');
  
  // View Mode
  const [viewMode, setViewMode] = useState('list'); // 'sections' | 'grid' | 'list'
  
  // Dialogs
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [associateDialogOpen, setAssociateDialogOpen] = useState(false);
  const [docToAssociate, setDocToAssociate] = useState(null);
  
  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDoc, setMenuDoc] = useState(null);
  
  // Text/Markdown content for viewer
  const [textContent, setTextContent] = useState('');
  const [loadingText, setLoadingText] = useState(false);
  
  // AI Doc Editor Drawer
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  
  // Edit Document Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [canEditDoc, setCanEditDoc] = useState(false);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isDev = user?.role === 'DEV' || user?.role === 'TECHNICIEN';
  const isInternalUser = isAdmin || isDev;
  const isGlobalTIAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN'; // Strictement admins GlobalTI

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      
      const subRes = await fetch(`/api/submissions/${submissionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubmission(subData);
      }

      const docsRes = await fetch(`/api/submissions/${submissionId}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData);
      }

      const optRes = await fetch(`/api/submissions/${submissionId}/documents/options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (optRes.ok) {
        const optData = await optRes.json();
        setAssociationOptions(optData);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [submissionId]);

  // Socket: join room and listen for document changes
  useEffect(() => {
    if (!socket || !submissionId) return;

    joinSubmission(submissionId);

    const handleDocumentChanged = (data) => {
      if (data.submissionId === submissionId) {
        fetchData();
      }
    };

    socket.on('documentChanged', handleDocumentChanged);

    return () => {
      leaveSubmission(submissionId);
      socket.off('documentChanged', handleDocumentChanged);
    };
  }, [socket, submissionId, joinSubmission, leaveSubmission]);

  // Filter and sort documents
  const filteredDocs = useMemo(() => {
    let result = documents.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
      const docType = getFileType(doc.name);
      const matchesType = typeFilter === 'all' || 
        (typeFilter === 'images' && docType === 'image') ||
        (typeFilter === 'documents' && docType !== 'image');
      const matchesVisibility = visibilityFilter === 'all' ||
        (visibilityFilter === 'public' && !doc.isAdminOnly) ||
        (visibilityFilter === 'private' && doc.isAdminOnly);
      
      // Section filter - inclusive: section filter shows docs from section AND its child items
      let matchesSection = sectionFilter === 'all';
      if (sectionFilter === 'general') {
        matchesSection = doc.association.type === 'general';
      } else if (sectionFilter.startsWith('section-')) {
        const sectionId = sectionFilter.replace('section-', '');
        // Match direct section association
        const directMatch = doc.association.type === 'section' && doc.association.id === sectionId;
        // Match items belonging to this section
        const itemMatch = doc.association.type === 'item' && doc.association.sectionId === sectionId;
        matchesSection = directMatch || itemMatch;
      } else if (sectionFilter.startsWith('item-')) {
        const itemId = sectionFilter.replace('item-', '');
        matchesSection = doc.association.type === 'item' && doc.association.id === itemId;
      }
      
      return matchesSearch && matchesType && matchesVisibility && matchesSection;
    });

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'type':
          return getFileType(a.name).localeCompare(getFileType(b.name));
        default:
          return 0;
      }
    });

    return result;
  }, [documents, searchTerm, typeFilter, visibilityFilter, sectionFilter, sortBy]);

  // Grouped documents for section view
  const groupedDocs = useMemo(() => {
    if (viewMode !== 'sections') return null;

    const general = filteredDocs.filter(d => d.association.type === 'general');
    
    const sections = associationOptions.map(section => {
      const sectionDocs = filteredDocs.filter(d => 
        d.association.type === 'section' && d.association.id === section.id
      );
      const items = section.items.map(item => ({
        ...item,
        docs: filteredDocs.filter(d => 
          d.association.type === 'item' && d.association.id === item.id
        )
      })).filter(item => item.docs.length > 0);

      return {
        ...section,
        docs: sectionDocs,
        items,
        totalDocs: sectionDocs.length + items.reduce((sum, item) => sum + item.docs.length, 0)
      };
    }).filter(s => s.totalDocs > 0);

    return { general, sections };
  }, [filteredDocs, viewMode, associationOptions]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('isAdminOnly', isAdmin);

    setUploading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/submissions/${submissionId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Upload error:', err);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleTogglePrivacy = async (docId) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/documents/${docId}/privacy`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Privacy toggle error:', err);
    }
  };

  const handleTogglePreview = async (docId) => {
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/documents/${docId}/preview`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Preview toggle error:', err);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document définitivement ?')) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // Force download instead of opening in new tab
  const handleDownload = async (doc) => {
    try {
      const response = await fetch(doc.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      // Fallback to opening in new tab
      window.open(doc.url, '_blank');
    }
  };

  const handleAssociate = async (associationType, associationId) => {
    if (!docToAssociate) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/documents/${docToAssociate.id}/associate`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ associationType, associationId })
      });
      setAssociateDialogOpen(false);
      setDocToAssociate(null);
      fetchData();
    } catch (err) {
      console.error('Associate error:', err);
    }
  };

  // === Fonctions d'édition de documents ===
  
  // Vérifier si un fichier est éditable (.md ou .txt)
  const isEditable = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['md', 'txt'].includes(ext);
  };

  // Ouvrir le dialog d'édition
  const openEditDialog = async (doc) => {
    setEditingDoc(doc);
    setEditDialogOpen(true);
    setEditLoading(true);
    setEditContent('');
    setCanEditDoc(false);
    
    try {
      const token = localStorage.getItem('portalToken');
      const res = await fetch(`/api/documents/${doc.id}/content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEditContent(data.content);
        setCanEditDoc(data.canEdit);
      } else {
        const errData = await res.json();
        setEditContent(`Erreur: ${errData.error}`);
      }
    } catch (err) {
      console.error('Error loading document content:', err);
      setEditContent('Erreur lors du chargement du contenu.');
    }
    setEditLoading(false);
  };

  // Sauvegarder les modifications
  const handleSaveEdit = async () => {
    if (!editingDoc) return;
    
    setEditSaving(true);
    try {
      const token = localStorage.getItem('portalToken');
      const res = await fetch(`/api/documents/${editingDoc.id}/content`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: editContent })
      });
      
      if (res.ok) {
        setEditDialogOpen(false);
        setEditingDoc(null);
        fetchData();
      } else {
        const errData = await res.json();
        alert(`Erreur: ${errData.error}`);
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Erreur lors de la sauvegarde');
    }
    setEditSaving(false);
  };

  const openViewer = async (doc) => {
    setSelectedDoc(doc);
    setViewerOpen(true);
    
    // Load text content for text/markdown files
    const fileType = getFileType(doc.name);
    if (fileType === 'text') {
      setLoadingText(true);
      setTextContent('');
      try {
        const response = await fetch(doc.url);
        const text = await response.text();
        setTextContent(text);
      } catch (err) {
        console.error('Error loading text content:', err);
        setTextContent('Erreur lors du chargement du contenu.');
      }
      setLoadingText(false);
    }
  };

  const openAssociateDialog = (doc) => {
    setDocToAssociate(doc);
    setAssociateDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleMenuOpen = (e, doc) => {
    setMenuDoc(doc);
    setMenuAnchor(e.currentTarget);
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || typeFilter !== 'all' || visibilityFilter !== 'all' || sectionFilter !== 'all';

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement...</Typography>;
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to={isAdmin ? '/admin/submissions' : '/portal/submissions'} color="inherit" underline="hover">
          Devis
        </Link>
        <Link 
          component={RouterLink} 
          to={isAdmin ? `/admin/submissions/${submissionId}` : `/portal/submissions/${submissionId}`} 
          color="inherit" 
          underline="hover"
        >
          {submission?.title || 'Devis'}
        </Link>
        <Typography color="text.primary">Documents</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FolderOpen size={32} color="#00d4aa" />
            Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {submission?.title} - {documents.length} document{documents.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={1.5}>
          {isGlobalTIAdmin && (
            <Button
              variant="outlined"
              startIcon={<Bot size={18} />}
              onClick={() => setAiDrawerOpen(true)}
              sx={{ 
                borderColor: 'rgba(139, 92, 246, 0.5)',
                color: '#8b5cf6',
                '&:hover': { 
                  borderColor: '#8b5cf6',
                  bgcolor: 'rgba(139, 92, 246, 0.1)'
                }
              }}
            >
              Assistant AI
            </Button>
          )}
          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={handleUpload}
            accept="image/*,.pdf,.doc,.docx,.txt,.md,.zip,.rar"
          />
          <Button
            variant="contained"
            startIcon={<Upload size={18} />}
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
          >
            {uploading ? 'Transfert...' : 'Ajouter un document'}
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
        {/* Row 1: Search and filters */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 180 }}
          />
          
          {/* Section Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Section</InputLabel>
            <Select
              value={sectionFilter}
              label="Section"
              onChange={(e) => setSectionFilter(e.target.value)}
            >
              <MenuItem value="all">Toutes les sections</MenuItem>
              <MenuItem value="general">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Unlink size={14} /> Général (non lié)
                </Box>
              </MenuItem>
              <Divider />
              {associationOptions.map((section) => [
                <MenuItem key={section.id} value={`section-${section.id}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Folder size={14} color="#3b82f6" /> {section.name}
                  </Box>
                </MenuItem>,
                ...section.items.map((item) => (
                  <MenuItem key={item.id} value={`item-${item.id}`} sx={{ pl: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChevronRight size={12} /> {item.name}
                    </Box>
                  </MenuItem>
                ))
              ])}
            </Select>
          </FormControl>

          {/* Type Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Tous types</MenuItem>
              <MenuItem value="images">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Image size={14} /> Images
                </Box>
              </MenuItem>
              <MenuItem value="documents">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FileText size={14} /> Documents
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Visibility Filter (Admin only) */}
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Visibilité</InputLabel>
              <Select
                value={visibilityFilter}
                label="Visibilité"
                onChange={(e) => setVisibilityFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="public">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Globe size={14} /> Publics
                  </Box>
                </MenuItem>
                <MenuItem value="private">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lock size={14} /> Privés
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Sort */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Trier par</InputLabel>
            <Select
              value={sortBy}
              label="Trier par"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date-desc">Date (récent)</MenuItem>
              <MenuItem value="date-asc">Date (ancien)</MenuItem>
              <MenuItem value="name-asc">Nom (A-Z)</MenuItem>
              <MenuItem value="name-desc">Nom (Z-A)</MenuItem>
              <MenuItem value="type">Type de fichier</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Row 2: View Mode */}
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="sections">
              <Layers size={16} style={{ marginRight: 6 }} /> Par Section
            </ToggleButton>
            <ToggleButton value="grid">
              <LayoutGrid size={16} style={{ marginRight: 6 }} /> Grille
            </ToggleButton>
            <ToggleButton value="list">
              <List size={16} style={{ marginRight: 6 }} /> Liste
            </ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="body2" color="text.secondary">
            {filteredDocs.length} résultat{filteredDocs.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Paper>

      {/* Documents Display */}
      {filteredDocs.length > 0 ? (
        <>
          {/* Section View */}
          {viewMode === 'sections' && groupedDocs && (
            <Box>
              {/* Sections with items */}
              {groupedDocs.sections.map((section) => (
                <Box key={section.id} sx={{ mb: 3 }}>
                  <SectionGroup
                    title={section.name}
                    icon={<Folder size={18} color="#3b82f6" />}
                    docs={section.docs}
                    isAdmin={isAdmin}
                    onView={openViewer}
                    onMenu={handleMenuOpen}
                  />
                  {section.items.map((item) => (
                    <Box key={item.id} sx={{ ml: 3 }}>
                      <SectionGroup
                        title={item.name}
                        icon={<ChevronRight size={16} color="#10b981" />}
                        docs={item.docs}
                        isAdmin={isAdmin}
                        onView={openViewer}
                        onMenu={handleMenuOpen}
                        level={1}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
              
              {/* General docs */}
              {groupedDocs.general.length > 0 && (
                <SectionGroup
                  title="Général (non lié)"
                  icon={<Unlink size={18} color="#9ca3af" />}
                  docs={groupedDocs.general}
                  isAdmin={isAdmin}
                  onView={openViewer}
                  onMenu={handleMenuOpen}
                />
              )}
            </Box>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <Grid container spacing={2}>
              {filteredDocs.map((doc) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                  <DocumentCard 
                    doc={doc} 
                    isAdmin={isAdmin} 
                    onView={openViewer} 
                    onMenu={handleMenuOpen}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <Paper sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.2)' }}>
                    <TableCell sx={{ width: 50 }}></TableCell>
                    <TableCell>Nom</TableCell>
                    <TableCell sx={{ width: 100 }}>Type</TableCell>
                    <TableCell>Section</TableCell>
                    <TableCell sx={{ width: 130 }}>Créé par</TableCell>
                    <TableCell sx={{ width: 120 }}>Modifié</TableCell>
                    {isAdmin && <TableCell sx={{ width: 80 }}>Visibilité</TableCell>}
                    <TableCell sx={{ width: 60 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocs.map((doc) => {
                    const fileType = getFileType(doc.name);
                    const assocColor = associationColors[doc.association.type];
                    
                    return (
                      <TableRow 
                        key={doc.id}
                        hover
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0, 212, 170, 0.05)' } }}
                        onClick={() => openViewer(doc)}
                      >
                        <TableCell>
                          <Tooltip 
                            title={fileType === 'image' ? (
                              <Box
                                component="img"
                                src={doc.url}
                                alt={doc.name}
                                sx={{ maxWidth: 200, maxHeight: 150 }}
                              />
                            ) : ''}
                            placement="right"
                          >
                            <Avatar 
                              variant="rounded" 
                              sx={{ 
                                width: 36, 
                                height: 36, 
                                bgcolor: 'rgba(0, 0, 0, 0.2)',
                                overflow: 'hidden'
                              }}
                            >
                              {fileType === 'image' ? (
                                <Box
                                  component="img"
                                  src={doc.url}
                                  alt={doc.name}
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <FileIcon type={fileType} size={20} />
                              )}
                            </Avatar>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {doc.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getFileTypeLabel(fileType)} 
                            size="small" 
                            sx={{ 
                              height: 22, 
                              fontSize: '0.65rem',
                              bgcolor: 'rgba(255, 255, 255, 0.05)'
                            }} 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={doc.association.name}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              bgcolor: assocColor.bg,
                              color: assocColor.color,
                              maxWidth: 180
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                            {doc.uploadedBy 
                              ? `${doc.uploadedBy.firstName || ''} ${doc.uploadedBy.lastName || ''}`.trim() || doc.uploadedBy.email
                              : 'Système'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`Créé le ${new Date(doc.createdAt).toLocaleDateString('fr-FR')}`}>
                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                              {new Date(doc.updatedAt || doc.createdAt).toLocaleDateString('fr-FR')}
                              {doc.updatedAt && new Date(doc.updatedAt).getTime() !== new Date(doc.createdAt).getTime() && (
                                <Typography component="span" variant="caption" sx={{ color: '#00d4aa', ml: 0.5 }}>•</Typography>
                              )}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            <Tooltip title={doc.isAdminOnly ? 'Privé' : 'Public'}>
                              <IconButton 
                                size="small" 
                                onClick={(e) => { e.stopPropagation(); handleTogglePrivacy(doc.id); }}
                              >
                                {doc.isAdminOnly ? <Lock size={16} color="#ef4444" /> : <Globe size={16} color="#10b981" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell>
                          {isAdmin && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, doc);
                              }}
                            >
                              <MoreVertical size={16} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      ) : (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.01)',
            border: '1px dashed rgba(255, 255, 255, 0.1)',
            borderRadius: 2
          }}
        >
          <FolderOpen size={48} color="#6b7280" style={{ marginBottom: 16 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {hasActiveFilters ? 'Aucun document trouvé' : 'Aucun document'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {hasActiveFilters
              ? 'Essayez avec d\'autres filtres'
              : 'Ajoutez des documents pour ce devis'}
          </Typography>
          {!hasActiveFilters && (
            <Button
              variant="contained"
              startIcon={<Upload size={18} />}
              onClick={() => fileInputRef.current.click()}
            >
              Ajouter un document
            </Button>
          )}
        </Paper>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { openViewer(menuDoc); setMenuAnchor(null); }}>
          <ListItemIcon><Eye size={16} /></ListItemIcon>
          <ListItemText>Visualiser</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleDownload(menuDoc); setMenuAnchor(null); }}>
          <ListItemIcon><Download size={16} /></ListItemIcon>
          <ListItemText>Télécharger</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => openAssociateDialog(menuDoc)}>
          <ListItemIcon><LinkIcon size={16} /></ListItemIcon>
          <ListItemText>Associer à...</ListItemText>
        </MenuItem>
        {/* Option d'édition pour .md et .txt */}
        {isEditable(menuDoc?.name) && (
          <MenuItem onClick={() => { openEditDialog(menuDoc); setMenuAnchor(null); }}>
            <ListItemIcon><Edit3 size={16} color="#00d4aa" /></ListItemIcon>
            <ListItemText>Modifier le contenu</ListItemText>
          </MenuItem>
        )}
        {isEditable(menuDoc?.name) && (
          <MenuItem onClick={() => { setHistoryDoc(menuDoc); setMenuAnchor(null); }}>
            <ListItemIcon><History size={16} /></ListItemIcon>
            <ListItemText>Historique des versions</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { handleTogglePrivacy(menuDoc?.id); setMenuAnchor(null); }}>
          <ListItemIcon>{menuDoc?.isAdminOnly ? <Globe size={16} /> : <Lock size={16} />}</ListItemIcon>
          <ListItemText>{menuDoc?.isAdminOnly ? 'Rendre public' : 'Rendre privé'}</ListItemText>
        </MenuItem>
        {getFileType(menuDoc?.name || '') === 'image' && (
          <MenuItem onClick={() => { handleTogglePreview(menuDoc?.id); setMenuAnchor(null); }}>
            <ListItemIcon><GalleryHorizontalEnd size={16} color={menuDoc?.isPreview ? '#8b5cf6' : undefined} /></ListItemIcon>
            <ListItemText>{menuDoc?.isPreview ? 'Retirer de Preview' : 'Marquer comme Preview'}</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => { handleDelete(menuDoc?.id); setMenuAnchor(null); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><Trash2 size={16} color="#ef4444" /></ListItemIcon>
          <ListItemText>Supprimer</ListItemText>
        </MenuItem>
      </Menu>

      {/* Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minHeight: '80vh' } }}
      >
        {selectedDoc && (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                <Typography variant="h6" noWrap sx={{ maxWidth: '50vw' }}>
                  {selectedDoc.name}
                </Typography>
                <Chip
                  label={selectedDoc.association.name}
                  size="small"
                  sx={{
                    bgcolor: associationColors[selectedDoc.association.type].bg,
                    color: associationColors[selectedDoc.association.type].color
                  }}
                />
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Télécharger">
                  <IconButton onClick={() => handleDownload(selectedDoc)} color="primary">
                    <Download size={20} />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={() => setViewerOpen(false)}>
                  <X size={20} />
                </IconButton>
              </Stack>
            </Box>
            
            <DialogContent sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: getFileType(selectedDoc.name) === 'text' ? 'flex-start' : 'center',
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              p: 2,
              overflow: 'auto'
            }}>
              {getFileType(selectedDoc.name) === 'image' ? (
                <Box
                  component="img"
                  src={selectedDoc.url}
                  alt={selectedDoc.name}
                  sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : getFileType(selectedDoc.name) === 'pdf' ? (
                <iframe
                  src={`${selectedDoc.url}#toolbar=1&navpanes=0`}
                  title={selectedDoc.name}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              ) : getFileType(selectedDoc.name) === 'text' ? (
                <Box sx={{ 
                  width: '100%', 
                  maxHeight: '70vh', 
                  bgcolor: 'rgba(255, 255, 255, 0.02)', 
                  borderRadius: 2, 
                  overflow: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.06)'
                }}>
                  <MarkdownViewer content={textContent} loading={loadingText} />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <FileIcon type={getFileType(selectedDoc.name)} size={64} />
                  <Typography variant="h6" sx={{ mt: 2 }}>Aperçu non disponible</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Download size={18} />}
                    onClick={() => handleDownload(selectedDoc)}
                    sx={{ mt: 2 }}
                  >
                    Télécharger
                  </Button>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Associate Dialog */}
      <Dialog
        open={associateDialogOpen}
        onClose={() => setAssociateDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon size={20} color="#00d4aa" />
          Associer le document
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {docToAssociate?.name}
          </Typography>
          
          <Stack spacing={1}>
            <Button
              fullWidth
              variant={docToAssociate?.association.type === 'general' ? 'contained' : 'outlined'}
              onClick={() => handleAssociate('general', null)}
              startIcon={<Unlink size={16} />}
              sx={{ justifyContent: 'flex-start' }}
            >
              Général (non lié)
            </Button>

            <Divider sx={{ my: 1 }}>Sections et éléments</Divider>

            {associationOptions.map((section) => (
              <Box key={section.id}>
                <Button
                  fullWidth
                  variant={docToAssociate?.association.type === 'section' && docToAssociate?.association.id === section.id ? 'contained' : 'text'}
                  onClick={() => handleAssociate('section', section.id)}
                  sx={{ 
                    justifyContent: 'flex-start', 
                    fontWeight: 600,
                    color: associationColors.section.color
                  }}
                >
                  {section.name}
                </Button>
                {section.items.map((item) => (
                  <Button
                    key={item.id}
                    fullWidth
                    variant={docToAssociate?.association.type === 'item' && docToAssociate?.association.id === item.id ? 'contained' : 'text'}
                    onClick={() => handleAssociate('item', item.id)}
                    sx={{ 
                      justifyContent: 'flex-start', 
                      pl: 4,
                      fontSize: '0.85rem',
                      color: associationColors.item.color
                    }}
                    startIcon={<ChevronRight size={14} />}
                  >
                    {item.name}
                  </Button>
                ))}
              </Box>
            ))}

            {associationOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Aucune section définie dans ce devis
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssociateDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* AI Doc Editor Drawer */}
      {isGlobalTIAdmin && (
        <DocEditorDrawer
          open={aiDrawerOpen}
          onClose={() => setAiDrawerOpen(false)}
          submissionId={submissionId}
          onDocumentsChanged={fetchData}
        />
      )}

      {/* Historique des versions */}
      <DocumentHistoryDialog
        open={Boolean(historyDoc)}
        onClose={() => setHistoryDoc(null)}
        documentId={historyDoc?.id}
        documentName={historyDoc?.name}
        onRestored={() => {
          fetchData();
          // Le contenu ouvert dans l'éditeur n'est plus à jour après une
          // restauration : on le referme plutôt que d'afficher du périmé.
          setEditDialogOpen(false);
        }}
      />

      {/* Edit Document Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editSaving && setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <Edit3 size={20} color="#00d4aa" />
          Modifier le document
          {editingDoc && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              - {editingDoc.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <Typography color="text.secondary">Chargement...</Typography>
            </Box>
          ) : !canEditDoc ? (
            <Box sx={{ p: 3 }}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <Typography color="error" sx={{ mb: 2, fontWeight: 600 }}>
                  Vous n'avez pas les droits pour modifier ce document.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Seul le créateur du document ou un administrateur peut le modifier.
                </Typography>
              </Paper>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Aperçu du contenu (lecture seule):
                </Typography>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
                  {editContent}
                </Box>
              </Box>
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Contenu du document..."
              InputProps={{
                sx: { 
                  fontFamily: 'monospace', 
                  fontSize: '0.85rem',
                  '& textarea': { minHeight: '50vh' }
                }
              }}
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 0,
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
          <Button
            onClick={() => setHistoryDoc(editingDoc)}
            startIcon={<History size={16} />}
            color="inherit"
            disabled={editSaving || !editingDoc}
            sx={{ mr: 'auto' }}
          >
            Historique
          </Button>
          <Button 
            onClick={() => setEditDialogOpen(false)} 
            disabled={editSaving}
          >
            {canEditDoc ? 'Annuler' : 'Fermer'}
          </Button>
          {canEditDoc && (
            <Button
              variant="contained"
              startIcon={editSaving ? null : <Save size={16} />}
              onClick={handleSaveEdit}
              disabled={editSaving}
            >
              {editSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DocumentsPage;
